import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
    Calendar,
    User,
    ArrowLeft,
    Share2,
    Facebook,
    Twitter,
    Link as LinkIcon,
    Clock,
    ChevronRight
} from 'lucide-react';
import SeoHead from '@/components/Seo';
import { fetchNewsBySlug } from '@/services/api';

interface NewsPost {
    id: number;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    author: string;
    category: string;
    published_at: string;
    image: string;
}

const NewsDetailPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [post, setPost] = useState<NewsPost | null>(null);
    const [related, setRelated] = useState<NewsPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                // Adjust endpoint to your Laravel API structure
                const response = await fetchNewsBySlug(slug);
                setPost(response.data.post);
                setRelated(response.data.related || []);
            } catch (err) {
                console.error("Post not found", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const shareToSocial = (platform: 'facebook' | 'twitter') => {
        const url = encodeURIComponent(`${window.location.origin}/news/${post?.slug}`);
        const title = encodeURIComponent(post?.title || '');

        const shareUrls = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`
        };

        window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    };

    const handleShare = async (e: React.MouseEvent, post: NewsPost) => {
        e.preventDefault(); // Stop the Link from opening
        e.stopPropagation(); // Stop the event from bubbling up

        const shareData = {
            title: post.title,
            text: post.excerpt,
            url: `${window.location.origin}/news/${post.slug}`,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: Copy to clipboard if Web Share API isn't available
                await navigator.clipboard.writeText(shareData.url);
                alert('Link copied to clipboard!');
            }
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    if (!post) return <div className="text-center py-20">Post not found.</div>;

    return (
        <div className="bg-[#fcfaf7] min-h-screen pb-20">
            <SeoHead title={post.title} description={post.excerpt} ogImage={post.image} />

            {/* Top Navigation Bar */}
            <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate('/news')}
                        className="flex items-center gap-2 text-slate-500 hover:text-amber-600 transition-colors group"
                    >
                        <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Back to News</span>
                    </button>
                    <div className="flex items-center gap-4">
                        <button className="p-2 text-slate-400 hover:text-slate-900"><Share2 size={18} /></button>
                    </div>
                </div>
            </nav>

            {/* Hero Header */}
            <header className="max-w-5xl mx-auto px-6 pt-12 pb-12 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                >
                    <span className="px-4 py-1.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold uppercase tracking-widest">
                        {post.category}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 leading-[1.1]">
                        {post.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-slate-400 text-sm">
                        <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-amber-500" />
                            {new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock size={16} className="text-amber-500" />
                            6 min read
                        </div>
                    </div>
                </motion.div>
            </header>

            {/* Featured Image */}
            <div className="max-w-7xl mx-auto px-4 mb-16">
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="aspect-[21/9] rounded-[3rem] overflow-hidden shadow-2xl shadow-slate-200"
                >
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                </motion.div>
            </div>

            {/* Content Section */}
            <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">

                {/* Sidebar Info */}
                <aside className="lg:col-span-3 space-y-12">
                    <div className="space-y-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Written by</p>
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">
                                {post.author.charAt(0)}
                            </div>
                            <div>
                                <p className="font-serif font-bold text-slate-900">{post.author}</p>
                                <p className="text-xs text-slate-500">Diocesan Press</p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-slate-200 space-y-4">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Spread the word</p>
                        <div className="flex flex-col gap-3">
                            {/* Facebook Button */}
                            <button
                                onClick={() => shareToSocial('facebook')}
                                className="flex items-center gap-3 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                            >
                                <Facebook size={18} /> Share on Facebook
                            </button>

                            {/* Twitter Button */}
                            <button
                                onClick={() => shareToSocial('twitter')}
                                className="flex items-center gap-3 text-sm text-slate-600 hover:text-sky-500 transition-colors"
                            >
                                <Twitter size={18} /> Share on Twitter
                            </button>
                            <button onClick={(e) => handleShare(e, post)} className="flex items-center gap-3 text-sm text-slate-600 hover:text-amber-600 transition-colors">
                                <LinkIcon size={18} /> Copy Link
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main Article Body */}
                <main className="lg:col-span-8 lg:col-start-5">
                    <div
                        className="prose prose-slate prose-lg max-w-none 
                        prose-headings:font-serif prose-headings:font-bold prose-headings:text-slate-900
                        prose-p:font-serif prose-p:italic prose-p:text-slate-600 prose-p:leading-relaxed
                        prose-strong:text-slate-900 prose-strong:not-italic
                        prose-blockquote:border-l-amber-500 prose-blockquote:bg-amber-50 prose-blockquote:py-2 prose-blockquote:rounded-r-xl"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                </main>
            </div>

            {/* Related Posts */}
            {related.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 mt-32 pt-20 border-t border-slate-200">
                    <h3 className="text-3xl font-serif font-bold text-slate-900 mb-12">Related Updates</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {related.map((item) => (
                            <Link key={item.id} to={`/news/${item.slug}`} className="group block space-y-4">
                                <div className="aspect-video rounded-3xl overflow-hidden bg-slate-100">
                                    <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                </div>
                                <h4 className="text-xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors leading-snug">
                                    {item.title}
                                </h4>
                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    Read Article <ChevronRight size={14} className="text-amber-500" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default NewsDetailPage;